import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  type ShouldRevalidateFunctionArgs,
} from "react-router";
import { Toaster } from "~/components/ui/toaster";
import { Toaster as Sonner } from "~/components/ui/sonner";
import { TooltipProvider } from "~/components/ui/tooltip";
import { ThemeProvider } from "~/components/ThemeProvider";
import { RouteErrorPage } from "~/components/RouteErrorPage";
import { ImpersonationBanner } from "~/components/ImpersonationBanner";
import { getImpersonator, getSafeUser } from "~/lib/auth/index.server";
import { env } from "~/lib/env.server";
import {
  isLeadManagementFormAction,
  isLeadManagementPath,
} from "~/lib/unassigned-count-revalidation";
import type { Route } from "./+types/root";

import "./index.css";

// The authenticated user for the whole app, read from the signed httpOnly
// session cookie on every document/data request. Client code reaches it through
// useAuth() (src/contexts/AuthContext.tsx), which reads this route's loader data. While
// an admin is viewing Cerbie as that user (better-auth's impersonation, ADR 0009), every
// screen says so.
export async function loader({ request }: Route.LoaderArgs) {
  const user = await getSafeUser(request);
  const impersonator = user ? await getImpersonator(request) : null;

  return {
    user,
    impersonation:
      user && impersonator ? { username: user.username, admin: impersonator.username } : null,
    appEnv: env.APP_ENV,
  };
}

// Lead filters and mutations do not change the signed-in user. Avoid reading it
// again for every search keystroke and inline edit.
export function shouldRevalidate({
  currentUrl,
  nextUrl,
  formMethod,
  formAction,
  defaultShouldRevalidate,
}: ShouldRevalidateFunctionArgs) {
  if (formMethod && formMethod !== "GET" && isLeadManagementFormAction(formAction, currentUrl)) {
    return false;
  }

  if (isLeadManagementPath(currentUrl.pathname) && isLeadManagementPath(nextUrl.pathname)) {
    return false;
  }

  return defaultShouldRevalidate;
}

// Default document metadata rendered through <Meta />. Individual routes export
// their own meta() to override the title (the whole descriptor array is replaced
// per matched route, not merged).
export function meta() {
  return [
    { title: "Cerbie Assistant" },
    { name: "description", content: "AI-powered sales enablement assistant by Cerbos" },
    { property: "og:title", content: "Cerbie Assistant" },
    { property: "og:description", content: "AI-powered sales enablement assistant" },
    { property: "og:type", content: "website" },
  ];
}

export function Layout({ children }: { children: React.ReactNode }) {
  return (
    // suppressHydrationWarning: next-themes mutates the class attribute pre-hydration.
    <html lang="en" suppressHydrationWarning>
      <head>
        <meta charSet="UTF-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Orbitron:wght@400;500;600;700;800;900&display=swap"
          rel="stylesheet"
        />
        <Meta />
        <Links />
      </head>
      <body>
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export default function App({ loaderData }: Route.ComponentProps) {
  return (
    <ThemeProvider attribute="class" defaultTheme="dark" enableSystem={false}>
      <TooltipProvider>
        {/* Unmissable marker that this isn't production (stg gets a periodic
            prod-data clone, so edits there are throwaway). pointer-events-none
            keeps it from ever swallowing clicks. */}
        {loaderData.appEnv !== "prd" && (
          <div className="pointer-events-none fixed inset-x-0 top-0 z-[100] flex justify-center">
            <span
              className={`rounded-b-md px-3 py-0.5 text-xs font-bold uppercase tracking-widest text-black shadow-md ${
                loaderData.appEnv === "stg" ? "bg-amber-500" : "bg-sky-400"
              }`}
            >
              {loaderData.appEnv === "stg" ? "Staging" : "Local"}
            </span>
          </div>
        )}
        {loaderData.impersonation && <ImpersonationBanner {...loaderData.impersonation} />}
        <Toaster />
        <Sonner />
        <Outlet />
      </TooltipProvider>
    </ThemeProvider>
  );
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  return <RouteErrorPage error={error} />;
}
